<template>
  <div class="flex main-container" id="ideas-container">
    <div
      v-if="slideActive.type === 'openEnded' && !isLoaded"
      class="horizontal layout center center-justified full-size"
    >
      <div class="text-center">
        <div class="quiz--result">
          {{ $t("loadingResult") }}...
        </div>
        <Loading class="mx-auto my-0" />
      </div>
    </div>

    <div
      v-if="slideActive.type === 'ideas' && slideActive.SlideOptions.length === 0"
      class="no-ideas"
      :class="{'white': isTextColourLight}"
    >
      {{ $t('noSubmissions') }}.
    </div>
    <div
      v-masonry
      class="ideas-container"
      horizontal-order="true"
      :transition-duration="`${masonryTransitionDuration}ms`"
      item-selector=".item"
      v-if="isContentVisible"
    >
      <div
        v-masonry-tile
        v-for="(item, index) in options"
        :key="item.id"
        class="item"
      >
        <div
          class="body-content"
          :class="[{'has-audience-info': item.audienceName
                      || item.audienceTeam || item.audienceEmoji || item.audienceEmail ||
                      slideActive.type === 'ideas',
                    reveal,
                    'result-step': isVotingStepResult,
          }]"
          @click="$emit('pickOption', item, index)"
        >
          <div
            class="body-content-front"
            :style="{
              'box-shadow': setBoxShadowValue(item, index),
              'background-color': getOpenEndedBackground(),
            }"
          >
            <div
              class="content-title"
            >
              <span
                class="content-rank-order"
                :style="{
                  'box-shadow': setBoxShadowValue(item, index),
                  'background-color': getRankOrderBg(),
                }"
              >
                <span
                  class="font-bold"
                  :class="{
                    'text-base': item.rankOrder <= 9,
                    'text-sm': item.rankOrder > 9 && item.rankOrder <= 99,
                    'text-xs': item.rankOrder > 99
                  }"
                >
                  {{ item.rankOrder }}
                </span>
              </span>
              <ui-pre-line
                class="content-title-text"
                :text="item.title"
                :class="{
                  'hide-submission': isBlurAnswerContent,
                  'has-transition': slideActive.type === 'ideas'
                }"
              />
            </div>
            <div
              class="audience-info"
              :class="{'dark': isTextColourLight,
                       'has-vote-count-content': slideActive.type === 'ideas'}"
              v-show="_checkShowAudienceInfo(item)"
            >
              <ParticipantInfo
                v-if="canUseCollectParticipantInfo"
                :submission="item"
              />
              <div v-else class="truncate">
                <div class="truncate">
                  <span
                    class="info--emoji"
                    v-if="isShownEmojiAvatar && _checkShowAudienceFields('avatar', item.audienceEmoji)"
                  >
                    {{ item.audienceEmoji }}
                  </span>
                  <span
                    v-if="_checkShowAudienceFields('name', item.audienceName)"
                    class="text-base font-bold"
                  >
                    <profanity-filter :text="item.audienceName" />
                  </span>
                </div>
                <div>
                  <p
                    class="info--other-team truncate"
                    v-if="_checkShowAudienceFields('team', item.audienceTeam)"
                  >
                    <profanity-filter :text="item.audienceTeam" />
                  </p>
                  <p
                    class="info--other-email truncate"
                    v-if="_checkShowAudienceFields('email', item.audienceEmail)"
                  >
                    <profanity-filter :text="item.audienceEmail" />
                  </p>
                </div>
              </div>
              <div
                class="info--vote-count"
                :class="{'opacity-0': isVotingStepSubmission}"
                v-if="slideActive.type === 'ideas'"
              >
                <aha-icon-container
                  size="xs"
                  icon-class="bg-base-0 text-base-90 vote-count-container container-custom-size"
                >
                  <aha-icon
                    name="like"
                    class="text-base-50 mb-0.5"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                  />
                  <span
                    class="pl-1 text-sm font-bold text-base-90"
                    v-show="isVoteCountTextVisible"
                  >{{ (item.votesCount || 0) | formatTotalPoint }}</span>
                </aha-icon-container>
              </div>
            </div>
            <span
              class="ml-2 mr-1 remove-icon"
              @click.stop="$emit('removeResponse', item, index)"
              v-if="removeResponseButtonVisible"
              :class="{'alway-show': item.deleting}"
            >
              <b-button v-tooltip="$i18nPresenter.t('Remove this response')" class="tooltip-btn" />
              <i class="fas fa-times-circle" v-if="!item.deleting" />
              <span
                v-else
                class="loading-spinner"
                :class="{'black': !isTextColourLight}"
              />
            </span>
          </div>
          <div
            class="body-content-back"
            :style="{
              'background-color': getAndSetRandomColor(item, index),
              'box-shadow': setBoxShadowValue(item, index),
            }"
          >
            <aha-icon
              class="text-base-0 opacity-65"
              name="idea"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import PreLine from '@/components/UI/PreLine.vue'
import ProfanityFilter from '@/components-v2/common/utils/ProfanityFilter.vue'
import Loading from '@/components/Loading'
import constants from '@/constant'
import Util from '@/util'
import EventBus from '@/eventBus'
import ParticipantInfo from '@/components/presentations/contents/ParticipantInfo.vue'

export default {
  components: {
    ProfanityFilter,
    UiPreLine: PreLine,
    Loading,
    ParticipantInfo,
  },
  props: {
    isLoaded: {
      type: Boolean,
      default: false,
    },
    options: {
      type: [Array, Object],
      required: true,
    },
    masonryTransitionDuration: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      tabName: constants.IdeasTabName,
      reveal: false,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      sharePresentation: (state) => state.sharePresentation,
    }),
    ...mapGetters(['isBackstage', 'isTextColourLight']),
    ...mapGetters('slideModule', ['modalBackgroundColour']),
    ...mapGetters('presentationSetting', ['isShownEmojiAvatar', 'canUseCollectParticipantInfo']),
    isContentVisible() {
      return this.slideActive.type === 'openEnded' ||
      (this.slideActive.type === 'ideas' && this.slideActive.SlideOptions.length !== 0)
    },
    isVotingStepSubmission() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.SUBMISSION
    },
    isVotingStepVoting() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.VOTING
    },
    isVotingStepResult() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.RESULT
    },
    removeResponseButtonVisible() {
      return ((!this.sharePresentation && this.slideActive.type === 'openEnded') ||
        (!this.sharePresentation && this.slideActive.type === 'ideas' &&
          this.slideActive.votingStep === this.tabName.SUBMISSION)) && !this.isBackstage
    },
    isBlurAnswerContent() {
      return !this.slideActive.showSubmissions && this.isVotingStepSubmission
    },
    isVoteCountTextVisible() {
      return (this.isVotingStepVoting && this.slideActive.showVotes) ||
        this.isVotingStepResult || this.isVotingStepSubmission
    },
  },
  watch: {
    '$store.state.presentation.fontFamily': function watchFontFamily() {
      this.redrawMasonry()
    },
    '$store.state.presentation.filteringProfanity': function watchFilteringProfanity() {
      this.redrawMasonry()
    },
    '$store.state.slideActive.votingStep': function watchVotingStep() {
      if (this.isVotingStepResult) {
        setTimeout(() => {
          this.reveal = true
        }, 1300)
      }
      this.reveal = false
    },
  },
  mounted() {
    this.redrawMasonry()
    EventBus.$on('on-reDraw-masonry', () => {
      this.redrawMasonry()
    })
    if (this.isVotingStepResult) {
      Util.disallowPDFRender()
      setTimeout(() => {
        this.reveal = true
        setTimeout(() => {
          Util.allowPDFRender()
        }, 9000)
      }, 1300)
    }
  },
  destroyed() {
    EventBus.$off('on-reDraw-masonry')
    Util.allowPDFRender()
  },
  methods: {
    _checkShowAudienceFields(fieldName, audienceInfo) {
      return Util.checkShowAudienceFields(fieldName, audienceInfo)
    },
    _checkShowAudienceInfo(answer) {
      return Util.checkShowAudienceInfo(answer)
    },
    setToColorPalette(item, index) {
      return Util.setToColorPalette(item, index)
    },
    setBoxShadowValue(item, index) {
      return `0 0 0 1.5px ${Util.setToColorPalette(item, index)}`
    },
    getAndSetRandomColor(item, index) {
      return Util.setToColorPalette(item, index)
    },
    getOpenEndedBackground() {
      const color = Util.hexToRgba(this.modalBackgroundColour)
      return Util.changeRgbaColor(color, 0.6)
    },
    getRankOrderBg() {
      return Util.hexToRgba(this.modalBackgroundColour)
    },
    redrawMasonry() {
      const DELAY = 900
      Util.disallowPDFRender()
      setTimeout(() => {
        this.$redrawVueMasonry()
        if (this.slideActive.type === 'openEnded') {
          setTimeout(() => {
            Util.allowPDFRender()
          }, this.masonryTransitionDuration + 2000)
        }
      }, DELAY)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
@import "@/assets/scss-v2/variables.scss";

.ideas-container {
  @apply w-full flex flex-row flex-wrap;
}
.no-ideas {
  font-weight: bold;
  color: #707070;
  padding-top: 16px;
  font-size: 20px;
  &.white {
    color: #ffffff;
    opacity: 0.8;
  }
}
.main-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -ms-overflow-style: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  .item {
    padding: 14px 11px 5px 11px;
    padding-left: 20px; // 20px to avoid rank badge truncated on hover
    width: 33%;
    z-index: 9;
    position: relative;

    .body-content {
      transition: all 0.25s;
      cursor: pointer;
      word-break: break-word;
      position: relative;
      padding: 10px 16px;

      .body-content-front, .body-content-back {
        border-radius: 8px;
      }
      .body-content-back {
        display: none;
      }
      .content-rank-order {
        display: none;
      }
      &.result-step {
        .content-rank-order {
          display: inline-block;
          @apply absolute -top-2.5 -left-2.5 w-7 h-7;
          @apply rounded-full z-10 flex items-center justify-center;
          color: inherit;
        }

        .body-content-back {
          @apply flex items-center justify-center;
        }
        .body-content-front, .body-content-back {
          transform-style: preserve-3d;
          backface-visibility: hidden;
          transition: transform 0.5s;
          transition-delay: 700ms;
          transform: rotateX(0deg); // https://bugzilla.mozilla.org/show_bug.cgi?id=1201471#c8
          will-change: transform; // optimization
        }
        .body-content-front {
          overflow: hidden; // this is needed to hide overflowing ranking part before flipping
          transform: rotateY(180deg);
        }
        .body-content-back {
          transform: rotateY(0);
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }

        &.reveal {
          .body-content-front {
            overflow: visible;
            transform: rotateY(0);
          }
          .body-content-back {
            transform: rotateY(180deg);
          }
        }
      }

      &:hover {
        transform: scale(1.05);
        .remove-icon {
          opacity: 0.5;
          &:hover {
            opacity: 1;
          }
        }
      }
    }
    &:hover {
      z-index: 20;
    }
  }
}

.has-audience-info {
  .info--other-team, .info--other-email {
    font-size: 0.85em;
  }
  &.body-content {
    padding: 0!important;
    .content-title {
      height: 80px;
      overflow-y: auto;
      -ms-overflow-style: auto;
      overflow-x: hidden;
      padding: 12px 16px;
      padding-bottom: 0px;

      .has-transition {
        transition: all 0.2s ease;
      }
      .hide-submission {
        filter: blur(4px);
        opacity: 0.2;
      }
    }
    .content-title-text {
      @apply flex-1 text-base inline-block;
      line-height: 22px;
    }
    .audience-info {
      padding: 9px 12px;
      line-height: 1.2;
      &.has-vote-count-content {
        display: flex;
        align-items: center;
        min-height: 40px;
      }
      .info--emoji {
        min-width: 20px;
        font-size: 15px;
        margin-top: 2px;
        display: inline-block;
      }
      .info--vote-count {
        @apply flex text-right flex-1 justify-end;
        .vote-count-container {
          &.container-custom-size {
            height: 1.7rem;
          }
          @apply w-max h-8 px-2 border border-base-50;
          margin-right: 0 !important; // overwrite bootstrap
        }
      }
    }
  }
  &.open-ended-title {
    padding: 0 !important;
    -webkit-overflow-scrolling: touch; //Chrome
    -ms-overflow-style: none; //IE, edge
    overflow: -moz-scrollbars-none; //Firefox
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    .title-text {
      padding: 0 30px 25px;
      max-height: 150px;
      min-height: 75px;
      overflow-y: auto;
      -ms-overflow-style: auto;
      margin-top: 25px;
    }
    .audience-info {
      padding: 12px 30px;
      line-height: 1.2;
      .info--emoji {
        min-width: 63px;
        font-size: 45px;
      }
    }
  }
  &.open-ended-picked {
    padding: 0 !important;
    overflow-y: hidden;
    .picked-title {
      padding: 0 30px 25px;
      margin-top: 25px;
      max-height: 202px;
      min-height: 81px;
      overflow-y: auto;
      -ms-overflow-style: auto;
    }
    .audience-info {
      padding: 12px 30px;
      line-height: 1.2;
      .info--emoji {
        min-width: 65px;
        font-size: 48px;
      }
    }
  }
}
.audience-info {
  color: inherit;
  border-radius: 0 0 8px 8px;
  background-color: rgba(240,241,241,1);
  &.dark {
    background-color: rgba(240,241,241,0.1);
  }
}
.remove-icon {
  position: absolute;
  top: 0;
  right: 0;
  margin-top: 0.2rem;
  opacity: 0;
  height: 20px;
  .fa-times-circle {
    color: $question-tooltips-color;
  }
  .tooltip-btn {
    position: absolute;
    padding: 0.55rem;
    opacity: 0;
    top: 1.5px;
  }

  &.alway-show {
    opacity: 1;
  }
}
</style>
